/**
 * Web stand-in for `react-native-webview`.
 *
 * react-native-web renders through react-dom, so an <iframe> is a faithful
 * browser equivalent of a native WebView. Supports the props this app and
 * zen-core-elements' <ZenWebView /> actually pass: source ({uri} | {html}),
 * style, onLoad/onLoadEnd/onError, onMessage and injectedJavaScript.
 *
 * Imperative handle mirrors the native one: postMessage / injectJavaScript /
 * reload / goBack / goForward.
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {View, StyleSheet} from 'react-native';

/*
 * Inline HTML (`source.html`) runs in a frame with no same-origin access: a
 * srcdoc frame with allow-same-origin shares the app's origin, so its scripts
 * could read the app's storage (login token) - no isolation at all. Without
 * same-origin the frame cannot be reached directly, so this bridge is written
 * into the document instead:
 *   - window.ReactNativeWebView.postMessage(...) -> onMessage, as on native
 *     (it was never defined before, so content calling it threw - the receipt
 *     preview never learned its height);
 *   - injectJavaScript / injectedJavaScript arrive as a message from the app.
 */
const INJECT_KEY = '__zbInjectJavaScript';
const BRIDGE_SCRIPT =
  '<script>(function(){' +
  'window.ReactNativeWebView={postMessage:function(m){parent.postMessage(String(m),"*");}};' +
  'window.addEventListener("message",function(e){' +
  `if(e.source===parent&&e.data&&typeof e.data.${INJECT_KEY}==="string"){(0,eval)(e.data.${INJECT_KEY});}` +
  '});})();</script>';

/** Bridge goes inside <head> (before the content's own scripts), never before the doctype. */
function withBridge(html: string): string {
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, match => match + BRIDGE_SCRIPT);
  }
  if (/<html[^>]*>/i.test(html)) {
    return html.replace(/<html[^>]*>/i, match => match + BRIDGE_SCRIPT);
  }
  return BRIDGE_SCRIPT + html;
}

export type WebViewSource = {uri?: string; html?: string; baseUrl?: string};

export type WebViewProps = {
  source?: WebViewSource;
  style?: any;
  containerStyle?: any;
  originWhitelist?: string[];
  textZoom?: number;
  injectedJavaScript?: string;
  onMessage?: (event: {nativeEvent: {data: string}}) => void;
  onLoad?: (event: any) => void;
  onLoadEnd?: (event: any) => void;
  onLoadStart?: (event: any) => void;
  onError?: (event: any) => void;
  scrollEnabled?: boolean;
  [key: string]: any;
};

export type WebViewHandle = {
  postMessage: (message: string) => void;
  injectJavaScript: (script: string) => void;
  reload: () => void;
  goBack: () => void;
  goForward: () => void;
  stopLoading: () => void;
};

const WebView = forwardRef<WebViewHandle, WebViewProps>(function WebView(props, ref) {
  const {
    source,
    style,
    containerStyle,
    injectedJavaScript,
    onMessage,
    onLoad,
    onLoadEnd,
    onLoadStart,
    onError,
  } = props;

  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const isInlineHtml = typeof source?.html === 'string';
  const srcDoc = useMemo(
    () => (isInlineHtml ? withBridge(source!.html as string) : undefined),
    [isInlineHtml, source?.html],
  );

  const runScript = useCallback((script: string) => {
    const frame = frameRef.current;
    if (!frame || !frame.contentWindow) {
      return;
    }
    if (isInlineHtml) {
      // Isolated frame: hand the script to the bridge instead of reaching in.
      frame.contentWindow.postMessage({[INJECT_KEY]: script}, '*');
      return;
    }
    try {
      // Only works for same-origin / srcDoc content, exactly like a native
      // WebView refuses to inject into cross-origin frames.
      (frame.contentWindow as any).eval(script);
    } catch (error) {
      console.warn('[WebView shim] injectJavaScript failed:', error);
    }
  }, [isInlineHtml]);

  useImperativeHandle(
    ref,
    () => ({
      postMessage: (message: string) => {
        const frame = frameRef.current;
        if (frame && frame.contentWindow) {
          frame.contentWindow.postMessage(message, '*');
        }
      },
      injectJavaScript: runScript,
      reload: () => {
        const frame = frameRef.current;
        if (frame) {
          // Reassigning src is the cross-browser safe reload for iframes.
          const current = frame.src;
          frame.src = current;
        }
      },
      goBack: () => window.history.back(),
      goForward: () => window.history.forward(),
      stopLoading: () => {},
    }),
    [runScript],
  );

  // Bridge window.postMessage back into the RN-style onMessage callback.
  useEffect(() => {
    if (!onMessage) {
      return undefined;
    }
    const handler = (event: MessageEvent) => {
      const frame = frameRef.current;
      if (frame && event.source === frame.contentWindow) {
        onMessage({nativeEvent: {data: String(event.data)}});
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onMessage]);

  const handleLoad = useCallback(
    (event: any) => {
      if (injectedJavaScript) {
        runScript(injectedJavaScript);
      }
      if (onLoad) {
        onLoad(event);
      }
      if (onLoadEnd) {
        onLoadEnd(event);
      }
    },
    [injectedJavaScript, onLoad, onLoadEnd, runScript],
  );

  useEffect(() => {
    if (onLoadStart) {
      onLoadStart({});
    }
    // Fire once per source change, like the native onLoadStart.
  }, [source?.uri, source?.html, onLoadStart]);

  return (
    <View style={[styles.container, containerStyle, style]}>
      {React.createElement('iframe', {
        ref: frameRef,
        src: isInlineHtml ? undefined : source?.uri,
        srcDoc,
        onLoad: handleLoad,
        onError,
        title: 'webview',
        // A native WebView is sandboxed from the host app. Inline HTML gets no
        // same-origin (see BRIDGE_SCRIPT); a remote page keeps it, which only
        // grants the page its *own* origin, never the app's.
        sandbox: isInlineHtml
          ? 'allow-scripts allow-forms allow-popups allow-modals'
          : 'allow-scripts allow-same-origin allow-forms allow-popups allow-modals',
        style: {border: 'none', width: '100%', height: '100%', flexGrow: 1, background: 'transparent'},
      })}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {flex: 1, overflow: 'hidden'},
});

export {WebView};
export default WebView;
